/**
 * Discount Education Popup — tree flow edition
 * Pure vanilla JS, no dependencies. Reads its content/config from a JSON
 * script tag rendered by the section (see discount-education-popup.liquid).
 *
 * The flow is a tree of named "sequences" (arrays of content/question
 * nodes). Traversal is a simple stack: start on the "main" sequence: when a
 * question is answered, push a new frame for "{questionKey}:{answerValue}"
 * on top of the stack (if that sequence exists) and drain it before
 * resuming "main" at the position right after the question. This supports
 * unlimited content blocks, unlimited questions, and branches that
 * themselves contain further questions, without any hardcoded step count.
 */
(function () {
  "use strict";

  var DEQ_VERSION = "tree-flow-v14-image-preload";
  window.__deqVersion = DEQ_VERSION;
  console.log("Discount popup script version:", DEQ_VERSION);

  function init() {
    var root = document.querySelector("[data-deq-root]");
    if (!root) return;

    var dataEl = document.getElementById(root.dataset.deqDataId);
    if (!dataEl) return;

    var config;
    try {
      config = JSON.parse(dataEl.textContent);
    } catch (e) {
      console.error("Discount popup: could not parse config JSON", e);
      return;
    }

    new DiscountQuizPopup(root, config).boot();
  }

  function DiscountQuizPopup(root, config) {
    this.root = root;
    this.config = config;
    this.overlay = root.querySelector(".deq-overlay");
    this.modalContainer = root.querySelector("[data-deq-modal-slot]");
    this.pill = root.querySelector("[data-deq-pill]");
    this.state = {
      shown: false,
      stack: [], // [{ seqKey, items, index }], top of stack = current sequence
      answers: {},
      completedCount: 0, // nodes fully passed, for progress estimation
    };
    this.dismissKey = "deq_dismissed_until";
    this.completedKey = "deq_completed";
    this.pillDismissedKey = "deq_pill_dismissed";
    this.viewedProductKey = "deq_viewed_product";
  }

  // On load: fully done (completed the quiz, or explicitly closed the
  // pill) means nothing shows at all. Previously dismissed the modal but
  // not the pill means show the pill immediately, skip the normal
  // timer/exit-intent triggers. Otherwise, normal trigger behavior.
  //
  // Product-view gate: if "require_product_view" is on, the popup is not
  // eligible to trigger (delay timer or exit intent) until the visitor has
  // been on a product page — either right now, or on an earlier page this
  // browser has visited (tracked via a long-lived cookie so it survives
  // navigating away from the product). This stops the discount offer from
  // appearing before someone has even reached a product.
  DiscountQuizPopup.prototype.boot = function () {
    if (getCookie(this.completedKey)) return;
    if (getCookie(this.pillDismissedKey)) return;

    if (this.config.settings.is_product_page) {
      setCookie(this.viewedProductKey, "1", 365);
    }

    if (this.config.settings.require_product_view && !getCookie(this.viewedProductKey)) {
      return;
    }

    var dismissedUntil = getCookie(this.dismissKey);
    if (dismissedUntil && Date.now() < parseInt(dismissedUntil, 10)) {
      this.showPill();
      return;
    }

    this.attachTriggers();
  };

  DiscountQuizPopup.prototype.attachTriggers = function () {
    var self = this;
    var cfg = this.config.settings;

    if (cfg.trigger_delay_seconds > 0) {
      this._delayTimer = setTimeout(function () {
        self.show();
      }, cfg.trigger_delay_seconds * 1000);
    }

    if (cfg.enable_exit_intent) {
      this._exitHandler = function (e) {
        if (e.clientY <= 0) {
          self.show();
        }
      };
      document.addEventListener("mouseout", this._exitHandler);
    }
  };

  DiscountQuizPopup.prototype.clearTriggers = function () {
    if (this._delayTimer) clearTimeout(this._delayTimer);
    if (this._exitHandler) document.removeEventListener("mouseout", this._exitHandler);
  };

  // Shows the full modal. Callable either from the normal timer/exit-intent
  // triggers, or from clicking the reopen pill — the completed check is
  // the only hard stop; a pending dismiss cookie doesn't block a manual
  // reopen, since clicking the pill is the person explicitly opting back in.
  DiscountQuizPopup.prototype.show = function () {
    if (this.state.shown || getCookie(this.completedKey)) return;
    this.state.shown = true;
    this.clearTriggers();
    this.hidePill();
    this.renderIntro();
    this.overlay.classList.add("is-visible");
    document.documentElement.style.overflow = "hidden";
  };

  DiscountQuizPopup.prototype.hide = function (permanent) {
    if (this._dwellTimer) clearTimeout(this._dwellTimer);
    this.overlay.classList.remove("is-visible");
    document.documentElement.style.overflow = "";
    this.state.shown = false;
    if (permanent) {
      var days = this.config.settings.dismiss_cookie_days || 7;
      setCookie(this.dismissKey, Date.now() + days * 86400000, days);
      this.showPill();
    }
  };

  // Floating reopen pill — shown after the modal is dismissed (rather than
  // it just vanishing entirely), so someone who closes it without reading
  // still has a low-friction way back in. Clicking the pill reopens the
  // modal fresh from the intro; the pill's own close button fully
  // dismisses it (and the modal) for the same duration as a normal
  // dismissal.
  DiscountQuizPopup.prototype.showPill = function () {
    var self = this;
    if (!this.pill) return;
    if (getCookie(this.completedKey) || getCookie(this.pillDismissedKey)) return;

    this.pill.innerHTML =
      '<span class="deq-pill-label">' + safe(this.config.settings.panel_title) + "</span>" +
      '<button type="button" class="deq-pill-close" data-deq-pill-close aria-label="Dismiss offer">&times;</button>';

    this.pill.classList.add("is-visible");
    // Double rAF so the entrance transition actually animates rather than
    // snapping straight to its final state (same trick used for the dwell
    // timer fill bar).
    requestAnimationFrame(function () {
      requestAnimationFrame(function () {
        self.pill.classList.add("is-animating-in");
      });
    });

    var closeBtn = this.pill.querySelector("[data-deq-pill-close]");
    closeBtn.addEventListener("click", function (e) {
      e.stopPropagation();
      self.hidePill();
      var days = self.config.settings.dismiss_cookie_days || 7;
      setCookie(self.pillDismissedKey, "1", days);
    });

    this.pill.onclick = function () {
      self.show();
    };
    this.pill.onkeydown = function (e) {
      if (e.key === "Enter" || e.key === " ") {
        e.preventDefault();
        self.show();
      }
    };
  };

  DiscountQuizPopup.prototype.hidePill = function () {
    if (!this.pill) return;
    this.pill.classList.remove("is-visible", "is-animating-in");
  };

  // ---------- Step orchestration (stack-based tree traversal) ----------

  DiscountQuizPopup.prototype.currentFrame = function () {
    return this.state.stack[this.state.stack.length - 1];
  };

  // Drop any frames that have been fully consumed, walking back down the
  // stack until we find one with items left, or the stack is empty.
  DiscountQuizPopup.prototype.settleStack = function () {
    var stack = this.state.stack;
    while (stack.length && stack[stack.length - 1].index >= stack[stack.length - 1].items.length) {
      stack.pop();
    }
  };

  // Rough progress estimate: completed nodes vs. completed + everything
  // still queued across the whole stack (current branch + all parent
  // branches waiting to resume). Recomputed every render, so it stays
  // reasonably accurate even though branch lengths vary.
  DiscountQuizPopup.prototype.progressPct = function () {
    var remaining = 0;
    for (var i = 0; i < this.state.stack.length; i++) {
      var f = this.state.stack[i];
      remaining += Math.max(0, f.items.length - f.index);
    }
    var completed = this.state.completedCount;
    var total = completed + remaining;
    return total > 0 ? Math.round((completed / total) * 100) : 0;
  };

  // ---------- Renderers ----------

  DiscountQuizPopup.prototype.renderIntro = function () {
    var c = this.config.intro;
    var self = this;
    this.modalContainer.innerHTML =
      '<div class="deq-modal deq-modal--intro deq-fade-enter">' +
        '<button class="deq-close" type="button" data-deq-close aria-label="Close">&times;</button>' +
        '<div class="deq-intro-content">' +
          (c.badge_image ? '<img class="deq-badge" src="' + attr(c.badge_image) + '"' + (c.badge_max_width ? ' style="max-width:' + c.badge_max_width + 'px"' : "") + ' alt="">' : "") +
          "<h2>" + safe(c.heading) + "</h2>" +
          "<p>" + safe(c.subheading) + "</p>" +
          (c.disclaimer ? '<p class="deq-disclaimer">' + safe(c.disclaimer) + "</p>" : "") +
          '<button class="deq-btn deq-btn-primary" type="button" data-deq-start>' + safe(c.cta_text) + "</button>" +
          '<button class="deq-btn deq-btn-ghost" type="button" data-deq-close>' + safe(c.dismiss_text) + "</button>" +
        "</div>" +
        (c.media_image ? '<div class="deq-intro-media" style="background-image:url(' + attr(c.media_image) + ')"></div>' : "") +
      "</div>";

    this.modalContainer.querySelectorAll("[data-deq-close]").forEach(function (btn) {
      btn.addEventListener("click", function () {
        self.hide(true);
      });
    });
    this.modalContainer.querySelector("[data-deq-start]").addEventListener("click", function () {
      var mainItems = self.config.sequences.main || [];
      self.state.stack = [{ seqKey: "main", items: mainItems, index: 0 }];
      self.state.completedCount = 0;
      self.renderCurrentNode();
    });
  };

  DiscountQuizPopup.prototype.panelShell = function (innerHtml, progressPct, stepLabel) {
    var self = this;
    this.modalContainer.innerHTML =
      '<div class="deq-modal deq-modal--panel deq-fade-enter">' +
        '<button class="deq-close" type="button" data-deq-close aria-label="Close">&times;</button>' +
        '<div class="deq-panel-header"><h3>' + safe(this.config.settings.panel_title) + "</h3></div>" +
        '<div class="deq-progress-track"><div class="deq-progress-fill" style="width:' + progressPct + '%"></div></div>' +
        '<div class="deq-panel-meta"><span>&#9200; ' + safe(this.config.settings.time_estimate) + "</span><span>" + safe(stepLabel) + "</span></div>" +
        '<div class="deq-panel-body">' + innerHtml + "</div>" +
      "</div>";

    this.modalContainer.querySelectorAll("[data-deq-close]").forEach(function (btn) {
      btn.addEventListener("click", function () {
        self.hide(true);
      });
    });
  };

  DiscountQuizPopup.prototype.renderQuestion = function (q, onAnswered, progressPct, stepLabel) {
    var self = this;
    var optionsHtml = q.options
      .map(function (opt, i) {
        return (
          '<label class="deq-option" data-deq-opt="' + attr(opt.value) + '">' +
            '<input type="radio" name="deq-q-' + attr(q.key) + '" value="' + attr(opt.value) + '">' +
            "<span>" + safe(opt.label) + "</span>" +
          "</label>"
        );
      })
      .join("");

    var otherHtml = q.allow_other
      ? '<label class="deq-option" data-deq-opt="__other__">' +
          '<input type="radio" name="deq-q-' + attr(q.key) + '" value="__other__">' +
          '<input type="text" class="deq-option-other" placeholder="Other..." data-deq-other-input>' +
        "</label>"
      : "";

    var html =
      "<h4>" + safe(q.question_text) + "</h4>" +
      '<div class="deq-options">' + optionsHtml + otherHtml + "</div>" +
      '<button class="deq-btn deq-btn-primary" type="button" data-deq-submit disabled>' + safe(q.submit_text) + "</button>";

    this.panelShell(html, progressPct, stepLabel);

    var body = this.modalContainer.querySelector(".deq-panel-body");
    var submitBtn = this.modalContainer.querySelector("[data-deq-submit]");
    var selectedValue = null;

    body.querySelectorAll(".deq-option").forEach(function (label) {
      label.addEventListener("click", function () {
        body.querySelectorAll(".deq-option").forEach(function (l) {
          l.classList.remove("is-selected");
        });
        label.classList.add("is-selected");
        label.querySelector('input[type="radio"]').checked = true;
        selectedValue = label.dataset.deqOpt;
        submitBtn.disabled = false;
        var otherInput = label.querySelector("[data-deq-other-input]");
        if (otherInput) {
          setTimeout(function () {
            otherInput.focus();
          }, 0);
        }
      });
    });

    submitBtn.addEventListener("click", function () {
      var value = selectedValue;
      if (value === "__other__") {
        var otherInput = body.querySelector("[data-deq-other-input]");
        value = "other:" + (otherInput ? otherInput.value.trim() : "");
      }
      if (!value) return;
      onAnswered(value);
    });
  };

  DiscountQuizPopup.prototype.renderContentScreen = function (screen) {
    var self = this;
    if (screen.image) {
      this.showLoadingPanel();
      preloadImage(screen.image, function () {
        self.renderContentScreenReady(screen);
      });
    } else {
      this.renderContentScreenReady(screen);
    }
  };

  // Lightweight spinner shown while an image preloads, so the screen
  // doesn't pop in with a layout jump once the image finishes loading —
  // or worse, briefly show a broken-image icon before it's ready.
  DiscountQuizPopup.prototype.showLoadingPanel = function () {
    var html = '<div class="deq-loading"><span class="deq-spinner"></span></div>';
    this.panelShell(html, this.progressPct(), "");
  };

  DiscountQuizPopup.prototype.renderContentScreenReady = function (screen) {
    var self = this;
    var layout = screen.image_layout || "full";
    var imgClass = layout === "float_left" ? "deq-img-float-left" : layout === "float_right" ? "deq-img-float-right" : "";
    var imgStyle = screen.image_max_width ? ' style="max-width:' + screen.image_max_width + 'px"' : "";
    var imgHtml = screen.image ? '<img class="' + imgClass + '" src="' + attr(screen.image) + '"' + imgStyle + ' alt="">' : "";

    var html = "<h4>" + safe(screen.heading) + "</h4>";
    if (layout === "full") {
      html += imgHtml + '<div class="deq-richtext">' + (screen.body || "") + "</div>";
    } else if (layout === "below") {
      html += '<div class="deq-richtext">' + (screen.body || "") + "</div>" + imgHtml;
    } else {
      // Floated image goes inside the richtext wrapper so CSS float + text wrap works correctly.
      html += '<div class="deq-richtext">' + imgHtml + (screen.body || "") + "</div>";
    }

    var minSeconds = this.config.settings.min_seconds_per_screen || 0;
    if (minSeconds > 0) {
      html +=
        '<button class="deq-btn deq-btn-primary deq-btn-timed" type="button" data-deq-continue disabled>' +
          '<span class="deq-btn-fill" data-deq-fill></span>' +
          '<span class="deq-btn-label">' + safe(screen.continue_text) + "</span>" +
        "</button>";
    } else {
      html += '<button class="deq-btn deq-btn-primary" type="button" data-deq-continue>' + safe(screen.continue_text) + "</button>";
    }

    this.panelShell(html, this.progressPct(), "");

    var continueBtn = this.modalContainer.querySelector("[data-deq-continue]");

    // Dwell timer intentionally starts here (after the image is already
    // loaded and shown) rather than when the image began preloading —
    // it's meant to measure time actually spent looking at the content,
    // not time spent waiting for it to appear.
    if (minSeconds > 0) {
      var fill = this.modalContainer.querySelector("[data-deq-fill]");
      fill.style.transitionDuration = minSeconds + "s";
      // Double rAF ensures the initial width:0% is painted before we
      // transition to 100%, so the fill actually animates instead of
      // jumping straight to full.
      requestAnimationFrame(function () {
        requestAnimationFrame(function () {
          fill.style.width = "100%";
        });
      });
      this._dwellTimer = setTimeout(function () {
        continueBtn.disabled = false;
      }, minSeconds * 1000);
    }

    continueBtn.addEventListener("click", function () {
      if (continueBtn.disabled) return;
      var frame = self.currentFrame();
      frame.index++;
      self.state.completedCount++;
      self.renderCurrentNode();
    });
  };

  DiscountQuizPopup.prototype.renderReward = function (r) {
    var self = this;
    this._activeReward = r;
    var html =
      '<div class="deq-reward">' +
        '<p class="deq-eyebrow">' + safe(r.eyebrow) + "</p>" +
        "<h2>" + safe(r.heading) + "</h2>" +
        '<button class="deq-btn deq-btn-primary" type="button" data-deq-claim>' + safe(r.cta_text) + "</button>" +
      "</div>";

    this.modalContainer.innerHTML =
      '<div class="deq-modal deq-modal--panel deq-fade-enter">' +
        '<button class="deq-close" type="button" data-deq-close aria-label="Close">&times;</button>' +
        html +
      "</div>";

    var self2 = this;
    this.modalContainer.querySelectorAll("[data-deq-close]").forEach(function (btn) {
      btn.addEventListener("click", function () {
        self2.hide(true);
      });
    });

    this.modalContainer.querySelector("[data-deq-claim]").addEventListener("click", function () {
      var claimBtn = self.modalContainer.querySelector("[data-deq-claim]");
      claimBtn.disabled = true;
      claimBtn.textContent = "One moment...";

      self.fetchDynamicCode(function (code) {
        self._activeReward = Object.assign({}, r, {
          discount_code: code || self.config.settings.backup_discount_code || "",
        });
        self.applyDiscountSilently();
        self.renderCodeReveal();
      });
    });
  };

  // Requests a freshly-minted, single-use discount code from the Worker
  // (added under the configured parent discount). Falls back to null on
  // any failure or timeout, letting the caller use the backup code instead
  // — a customer should never be left completely empty-handed.
  DiscountQuizPopup.prototype.fetchDynamicCode = function (callback) {
    var endpoint = this.config.settings.capture_endpoint;
    var parentTitle = this.config.settings.parent_discount_title;
    if (!endpoint || !parentTitle) {
      callback(null);
      return;
    }

    var url = endpoint.replace(/\/$/, "") + "/issue-code";
    var controller = typeof AbortController !== "undefined" ? new AbortController() : null;
    var timeoutId = setTimeout(function () {
      if (controller) controller.abort();
    }, 6000);

    fetch(url, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        parentDiscountTitle: parentTitle,
        expiryHours: this.config.settings.code_expiry_hours || 72,
      }),
      signal: controller ? controller.signal : undefined,
    })
      .then(function (res) {
        return res.json();
      })
      .then(function (body) {
        clearTimeout(timeoutId);
        callback(body && body.ok && body.code ? body.code : null);
      })
      .catch(function (err) {
        clearTimeout(timeoutId);
        console.warn("Discount popup: dynamic code issuance failed, using backup code.", err);
        callback(null);
      });
  };

  DiscountQuizPopup.prototype.renderCodeReveal = function () {
    var c = this._activeReward;
    var self = this;
    var html =
      '<div class="deq-reward">' +
        "<p>" + safe(c.confirm_eyebrow) + "</p>" +
        "<h2>" + safe(c.confirm_heading) + "</h2>" +
        '<div class="deq-code-box"><span>' + safe(c.discount_code) + '</span><span class="deq-check">&#10003;</span></div>' +
        '<p class="deq-applied-note">' + safe(c.applied_note) + "</p>" +
      "</div>";

    this.modalContainer.innerHTML =
      '<div class="deq-modal deq-modal--panel deq-fade-enter">' +
        '<button class="deq-close" type="button" data-deq-close aria-label="Close">&times;</button>' +
        html +
      "</div>";

    this.modalContainer.querySelectorAll("[data-deq-close]").forEach(function (btn) {
      btn.addEventListener("click", function () {
        self.hide(false);
      });
    });

    var completedDays = this.config.settings.completed_cookie_days || 60;
    setCookie(this.completedKey, "1", completedDays);
  };

  DiscountQuizPopup.prototype.renderQuestionNode = function (q) {
    var self = this;
    this.renderQuestion(
      q,
      function (value) {
        self.state.answers[q.key] = value;
        var frame = self.currentFrame();
        frame.index++;
        self.state.completedCount++;

        var branchKey = q.key + ":" + value;
        var branchItems = self.config.sequences[branchKey];
        if (branchItems && branchItems.length) {
          self.state.stack.push({ seqKey: branchKey, items: branchItems, index: 0 });
        }
        self.renderCurrentNode();
      },
      this.progressPct(),
      ""
    );
  };

  DiscountQuizPopup.prototype.renderCaptureScreen = function (node) {
    var self = this;
    var html =
      "<h4>" + safe(node.heading) + "</h4>" +
      (node.subheading ? "<p>" + safe(node.subheading) + "</p>" : "") +
      '<div class="deq-field-group">' +
        '<input type="text" class="deq-input" data-deq-first-name placeholder="' + attr(node.first_name_label) + '" required>' +
        (node.collect_last_name ? '<input type="text" class="deq-input" data-deq-last-name placeholder="' + attr(node.last_name_label) + '">' : "") +
        '<input type="email" class="deq-input" data-deq-email placeholder="' + attr(node.email_label) + '" required>' +
      "</div>" +
      '<button class="deq-btn deq-btn-primary" type="button" data-deq-capture-submit>' + safe(node.submit_text) + "</button>" +
      '<div class="deq-consent-text deq-richtext">' + (node.consent_text || "") + "</div>";

    this.panelShell(html, this.progressPct(), "");

    this.modalContainer.querySelector("[data-deq-capture-submit]").addEventListener("click", function () {
      var firstName = self.modalContainer.querySelector("[data-deq-first-name]").value.trim();
      var lastNameEl = self.modalContainer.querySelector("[data-deq-last-name]");
      var lastName = lastNameEl ? lastNameEl.value.trim() : "";
      var email = self.modalContainer.querySelector("[data-deq-email]").value.trim();

      if (!firstName || !email || email.indexOf("@") === -1) {
        self.modalContainer.querySelector("[data-deq-capture-submit]").insertAdjacentHTML(
          "beforebegin",
          '<p style="color:#b3261e;font-size:13px;margin:0 0 10px;">Please enter your first name and a valid email.</p>'
        );
        return;
      }

      self.state.answers._first_name = firstName;
      self.state.answers._email = email;

      var tags = (node.extra_tags || []).slice();
      Object.keys(self.state.answers).forEach(function (k) {
        if (k.indexOf("_") !== 0) tags.push(self.state.answers[k]); // skip internal _first_name/_email
      });

      self.submitCaptureToShopify({
        firstName: firstName,
        lastName: lastName,
        email: email,
        tags: tags,
      });

      var frame = self.currentFrame();
      frame.index++;
      self.state.completedCount++;
      self.renderCurrentNode();
    });
  };

  // POSTs to a small serverless endpoint (see /worker in the project files)
  // that creates/tags the customer via Shopify's Admin API, server-to-
  // server. This exists because Shopify's public /contact form requires a
  // CAPTCHA token that no scripted request can produce — Admin API calls
  // aren't subject to that gate, since it's a public-facing anti-bot
  // measure rather than an API-level restriction. Also gives us real
  // support for untruncated tags, not reliable via /contact.
  // NOTE: posts to the bare endpoint URL (root path) — the Worker treats
  // POST / as the capture route, and POST /issue-code as the discount
  // code route (see fetchDynamicCode below).
  DiscountQuizPopup.prototype.submitCaptureToShopify = function (data) {
    var endpoint = this.config.settings.capture_endpoint;
    if (!endpoint) {
      console.warn("Discount popup: no capture_endpoint configured, skipping submission.");
      return;
    }
    try {
      fetch(endpoint, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          firstName: data.firstName,
          lastName: data.lastName,
          email: data.email,
          tags: data.tags,
        }),
      })
        .then(function (res) {
          return res.json().then(function (body) {
            console.log("Discount popup: capture endpoint response", res.status, body);
          });
        })
        .catch(function (err) {
          console.warn("Discount popup: capture endpoint request failed", err);
        });
    } catch (err) {
      console.warn("Discount popup: customer capture failed to send", err);
    }
  };

  DiscountQuizPopup.prototype.renderCurrentNode = function () {
    this.settleStack();
    var frame = this.currentFrame();
    if (!frame) {
      // Stack drained without ever hitting an explicit Reward block —
      // safety net so the flow doesn't dead-end. Add a Reward block to
      // whichever sequence this happened in to replace this fallback.
      console.warn("Discount popup: reached end of flow with no Reward block in this path. Add one.");
      this.renderReward({
        eyebrow: "You earned a discount!",
        heading: "Congrats, you've earned a discount",
        cta_text: "Claim my reward",
        confirm_eyebrow: "Congrats! You've earned",
        confirm_heading: "a discount",
        applied_note: "We've applied this discount code to your cart.",
      });
      return;
    }
    var node = frame.items[frame.index];
    if (node.type === "content") {
      this.renderContentScreen(node);
    } else if (node.type === "question") {
      this.renderQuestionNode(node);
    } else if (node.type === "capture") {
      this.renderCaptureScreen(node);
    } else if (node.type === "reward") {
      // Terminal: whichever reward node is reached ends the flow here,
      // regardless of what else is still queued in the stack.
      this.renderReward(node);
    }
  };

  // ---------- Discount application (no page reload, no admin API) ----------
  // Loads Shopify's native "/discount/CODE" link inside a hidden iframe.
  // Shopify sets its checkout-session discount cookie as a side effect of
  // that request, without navigating the visible page away.
  DiscountQuizPopup.prototype.applyDiscountSilently = function () {
    var code = this._activeReward && this._activeReward.discount_code;
    if (!code) return;
    var iframe = document.createElement("iframe");
    iframe.style.display = "none";
    iframe.setAttribute("aria-hidden", "true");
    iframe.src = "/discount/" + encodeURIComponent(code) + "?redirect=/";
    document.body.appendChild(iframe);
    setTimeout(function () {
      if (iframe.parentNode) iframe.parentNode.removeChild(iframe);
    }, 4000);
  };

  // ---------- Helpers ----------

  // Preloads an image and calls back once it's loaded, errored, or a
  // timeout elapses — whichever comes first. The timeout ensures a broken
  // or slow-loading image never leaves the popup stuck showing a spinner
  // forever.
  function preloadImage(url, callback, timeoutMs) {
    var done = false;
    var finish = function () {
      if (done) return;
      done = true;
      callback();
    };
    var img = new Image();
    img.onload = finish;
    img.onerror = finish;
    img.src = url;
    setTimeout(finish, timeoutMs || 5000);
  }

  function safe(str) {
    if (str === undefined || str === null) return "";
    var div = document.createElement("div");
    div.textContent = str;
    return div.innerHTML;
  }

  function attr(str) {
    return (str || "").replace(/"/g, "&quot;");
  }

  function setCookie(name, value, days) {
    var expires = new Date(Date.now() + days * 86400000).toUTCString();
    document.cookie = name + "=" + encodeURIComponent(value) + "; expires=" + expires + "; path=/; SameSite=Lax";
  }

  function getCookie(name) {
    var match = document.cookie.match(new RegExp("(?:^|; )" + name + "=([^;]*)"));
    return match ? decodeURIComponent(match[1]) : null;
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init);
  } else {
    init();
  }
})();